
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { FileCheck, Bell, Clock, Plus } from "lucide-react";
import { 
  StatsCards, 
  DistributionChart, 
  WeeklyResultsChart, 
  RecentActivity, 
  DetectionTypes,
  YearlyGrowthChart
} from "@/components/dashboard";

// Memoized chart data to prevent recalculations on each render
const pieData = [
  { name: "Text", value: 35, color: "#0EA5E9" },
  { name: "Image", value: 25, color: "#06B6D4" },
  { name: "Video", value: 15, color: "#0284C7" },
  { name: "Voice", value: 10, color: "#0369A1" },
  { name: "Document", value: 8, color: "#0891B2" },
  { name: "Plagiarism", value: 5, color: "#0E7490" },
  { name: "Proxy", value: 2, color: "#155E75" },
];

const barData = [
  { name: "Mon", fraudulent: 12, suspicious: 19, safe: 69 },
  { name: "Tue", fraudulent: 15, suspicious: 21, safe: 64 },
  { name: "Wed", fraudulent: 18, suspicious: 25, safe: 57 },
  { name: "Thu", fraudulent: 14, suspicious: 23, safe: 63 },
  { name: "Fri", fraudulent: 20, suspicious: 28, safe: 52 },
  { name: "Sat", fraudulent: 8, suspicious: 15, safe: 77 },
  { name: "Sun", fraudulent: 7, suspicious: 13, safe: 80 },
];

const lineData = [
  { name: "Jan", value: 100 },
  { name: "Feb", value: 120 },
  { name: "Mar", value: 150 },
  { name: "Apr", value: 180 },
  { name: "May", value: 230 },
  { name: "Jun", value: 280 },
  { name: "Jul", value: 340 },
  { name: "Aug", value: 390 },
  { name: "Sep", value: 420 },
  { name: "Oct", value: 450 },
  { name: "Nov", value: 500 },
  { name: "Dec", value: 580 },
];

const recentActivity = [
  {
    type: "Image",
    name: "profile-photo.jpg",
    status: "fraudulent",
    time: "2 minutes ago",
  },
  {
    type: "Text",
    name: "scholarship-essay.txt",
    status: "safe",
    time: "15 minutes ago",
  },
  {
    type: "Voice",
    name: "interview-recording.mp3",
    status: "suspicious",
    time: "1 hour ago",
  },
  {
    type: "Document",
    name: "contract-v2.pdf",
    status: "safe",
    time: "3 hours ago",
  },
  {
    type: "Video",
    name: "product-demo.mp4",
    status: "fraudulent",
    time: "Yesterday",
  },
];

const statsCards = [
  {
    title: "Total Analyses",
    value: "1,234",
    description: "Across all detection types",
    icon: FileCheck,
    trend: { value: 12, isPositive: true },
  },
  {
    title: "Fraud Detected",
    value: "187",
    description: "15.2% of total content",
    icon: Bell,
    trend: { value: 8, isPositive: true },
  },
  {
    title: "Analysis Time",
    value: "2.3s",
    description: "Average processing time",
    icon: Clock,
    trend: { value: 5, isPositive: false },
  },
];

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Monitor and analyze your fraud detection activities</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Clock className="mr-2 h-4 w-4" />
            View History
          </Button>
          <Button className="bg-fraudeye-accent hover:bg-fraudeye-highlight" size="sm">
            <Plus className="mr-2 h-4 w-4" />
            New Analysis
          </Button>
        </div>
      </div>

      <StatsCards statsCards={statsCards} />

      <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full md:w-auto grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>
        
        {/* Only render the active tab content */}
        {activeTab === "overview" && (
          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <DistributionChart data={pieData} />
              <WeeklyResultsChart data={barData} />
            </div>
            <RecentActivity activities={recentActivity} />
          </TabsContent>
        )}
        
        {activeTab === "activity" && (
          <TabsContent value="activity" className="space-y-4">
            <DetectionTypes />
          </TabsContent>
        )}
        
        {activeTab === "analytics" && (
          <TabsContent value="analytics" className="space-y-4">
            <YearlyGrowthChart data={lineData} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Dashboard;
